import { of, Observable, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { Injectable, EventEmitter } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from "@angular/common/http";
import { environment } from '../../../environments/environment';
import { UtilServiceService } from '../utilService/util-service.service';

@Injectable({
  providedIn: 'root'
})
export class SettingService {

  refreshList$ = new EventEmitter();
  refreshTenant$ = new EventEmitter();
  refreshOrg$ = new EventEmitter();
  
  private ENDPOINT_ORG_SETTINGS = "/get-org-settings/";
  private ENDPOINT_LICENSE_INFO = "/get-licenses";
  private ENDPOINT_UPDATE_ORG_SETTINGS = "/update-org-settings";
  private ENDPOINT_CONNECT_SALESFORCE = "/saleforce/authenticate";
  private ENDPOINT_DISCONNECT_SALESFORCE = "/saleforce/disconnect";
  private ENDPOINT_ORG_SF_SETTINGS = "/get-org-settings/1/salesforce-settings";
  private ENDPOINT_UPDATE_ORG_SF_SETTINGS = "/update-org-sf-settings";
  private ENDPOINT_FLEET_CONFIG_ORG = "/fleet-configs/org";
  private ENDPOINT_FLEET_CONFIG = "/fleet-configs";

  constructor(
    private http: HttpClient,
    private util: UtilServiceService
  ) {
    this.refreshList$ = new EventEmitter();
    this.refreshOrg$ = new EventEmitter();
    this.refreshTenant$ = new EventEmitter();
  }

  public getOrgSettings(id: any): Observable<any> {
    return this.http.get(environment.base_url + this.ENDPOINT_ORG_SETTINGS + id).pipe(
      map((resp: any) => resp),
      catchError(error => {
        console.error('Get Org Settings Error:', error);
        return of(error);
      })
    );
  }
  
  public getPartOrders(id: any): Observable<any> {
    return this.http.get(environment.base_url + '/part-orders/get-all?jobOrderId=' + id).pipe(
      map((resp: any) => resp),
      catchError(error => {
        console.error('Get Part Orders Error:', error);
        return of(error);
      })
    );
  }

  public updateOrgSettings(body: any): Observable<any> {
    return this.http.put(environment.base_url + this.ENDPOINT_UPDATE_ORG_SETTINGS, body, { observe: 'response' }).pipe(
      map((resp: any) => {
        this.util.setCompanyName(resp.body.companyName);
        this.refreshList$.emit(resp);
        return resp;
      }),
      catchError(error => {
        console.error('Update Org Settings Error:', error);
        return of(error);
      })
    );
  }

  public getLicenseInfo(): Observable<any> {
    return this.http.get(environment.base_url + this.ENDPOINT_LICENSE_INFO).pipe(
      map((resp: any) => resp),
      catchError(error => {
        console.error('Get License Info Error:', error);
        return of(error);
      })
    );
  }

  public connectToSalesforce(body: any): Observable<any> {
    return this.http.post(environment.base_url + this.ENDPOINT_CONNECT_SALESFORCE, body, { observe: 'response' }).pipe(
      map((resp: any) => resp),
      catchError(error => {
        console.error('Connect to Salesforce Error:', error);
        return of(error);
      })
    );
  }

  public disconnectFromSalesforce(): Observable<any> {
    return this.http.get(environment.base_url + this.ENDPOINT_DISCONNECT_SALESFORCE).pipe(
      map((resp: any) => {
        this.refreshOrg$.emit(resp);
        return resp;
      }),
      catchError(error => {
        console.error('Disconnect from Salesforce Error:', error);
        return of(error);
      })
    );
  }

  public getOrgSFSettings(): Observable<any> {
    return this.http.get(environment.base_url + this.ENDPOINT_ORG_SF_SETTINGS).pipe(
      map((resp: any) => resp),
      catchError(error => {
        console.error('Get Org SF Settings Error:', error);
        return of(error);
      })
    );
  }

  public updateOrgSFSettings(body: any): Observable<any> {
    return this.http.put(environment.base_url + this.ENDPOINT_UPDATE_ORG_SF_SETTINGS, body, { observe: 'response' }).pipe(
      map((resp: any) => {
        this.refreshOrg$.emit(resp);
        return resp;
      }),
      catchError(error => {
        console.error('Update Org SF Settings Error:', error);
        return of(error);
      })
    );
  }

  public updateAlertSettings(value: any, bool: any, distance: any): Observable<any> {
    return this.http.put(
      `${environment.base_url}/update-org-settings/update-alerts/1?alertValue=${value}&alertFlag=${bool}&taskCheckinCheckoutDistanceValue=${distance}`, 
      {}, // ✅ Added empty body
      { observe: 'response' }
    ).pipe(
      map((resp: any) => {
        this.util.setCompanyName(resp.body.companyName);
        this.refreshList$.emit(resp);
        return resp;
      }),
      catchError(error => {
        console.error('Update Alert Settings Error:', error);
        return of(error);
      })
    );
  }

  public updateUserAlertSettings(value: any, bool: any): Observable<any> {
    return this.http.put(
      `${environment.base_url}/users/alert?alertForTaskTime=${value}&isTaskAlert=${bool}`, 
      {}, // ✅ Added empty body
      { observe: 'response' }
    ).pipe(
      map((resp: any) => {
        this.util.setCompanyName(resp.body.companyName);
        this.refreshList$.emit(resp);
        return resp;
      }),
      catchError(error => {
        console.error('Update User Alert Settings Error:', error);
        return of(error);
      })
    );
  }
  
  public getTenantList(): Observable<any> {
    return this.http.get(environment.base_url + '/tenant-addon-features', { observe: 'response' }).pipe(
      map((resp: any) => {
        this.refreshTenant$.emit(resp);
        return resp;
      }),
      catchError(error => {
        console.error('Get Tenant List Error:', error);
        return of(error);
      })
    );
  }
  
  public getTenantPackageList(): Observable<any> {
    return this.http.get(environment.base_url + '/tenant-package-features', { observe: 'response' }).pipe(
      map((resp: any) => {
        this.refreshTenant$.emit(resp);
        return resp;
      }),
      catchError(error => {
        console.error('Get Tenant Package List Error:', error);
        return of(error);
      })
    );
  }
  
  /************* Get Tenant Package List To Hide  *********/
  public getTenantPackageListToHide(): Observable<any> {
    return this.http.get(environment.base_url + '/tenant-package-hidden-features', { observe: 'response' }).pipe(
      map((resp: any) => {
        this.refreshTenant$.emit(resp);
        return resp;
      }),
      catchError(error => {
        console.error('Get Tenant Package List To Hide Error:', error);
        return of(error);
      })
    );
  }

  // -------------------------------------------fleet configuration-----------------------------------
  public getFleetConfig(): Observable<any> {
    return this.http.get(environment.base_url + this.ENDPOINT_FLEET_CONFIG_ORG, { observe: 'response' }).pipe(
      map((resp: any) => resp),
      catchError(error => {
        console.error('Get Fleet Config Error:', error);
        return of(error);
      })
    );
  }

  public editFleetConfig(body: any): Observable<any> {
    return this.http.put(environment.base_url + this.ENDPOINT_FLEET_CONFIG, body, { observe: 'response' }).pipe(
      map((resp: any) => {
        this.refreshList$.emit(resp);
        return resp;
      }),
      catchError(error => {
        console.error('Edit Fleet Config Error:', error);
        return of(error);
      })
    );
  }
  
  // -------------------------------------------JO configuration-----------------------------------
  public getJOSettings(): Observable<any> {
    return this.http.get(environment.base_url + '/job-order-config').pipe(
      map((resp: any) => resp),
      catchError(error => {
        console.error('Get JO Settings Error:', error);
        return of(error);
      })
    );
  }
  
  public updateJoSettings(body: any): Observable<any> {
    return this.http.put(environment.base_url + '/job-order-configs', body, { observe: 'response' }).pipe(
      map((resp: any) => {
        this.refreshList$.emit(resp);
        return resp;
      }),
      catchError(error => {
        console.error('Update JO Settings Error:', error);
        return of(error);
      })
    );
  }
  
  // -------------------------------------------SO configuration-----------------------------------
  public getSOSettings(): Observable<any> {
    return this.http.get(environment.base_url + '/sales-order-config').pipe(
      map((resp: any) => resp),
      catchError(error => {
        console.error('Get SO Settings Error:', error);
        return of(error);
      })
    );
  }
  
  public updateSOSettings(body: any): Observable<any> {
    return this.http.put(environment.base_url + '/sales-order-configs', body, { observe: 'response' }).pipe(
      map((resp: any) => {
        this.refreshList$.emit(resp);
        return resp;
      }),
      catchError(error => {
        console.error('Update SO Settings Error:', error);
        return of(error);
      })
    );
  }

  // -------------------------------------------Project configuration-----------------------------------
  public getProjectSettings(): Observable<any> {
    return this.http.get(environment.base_url + '/project-configs').pipe(
      map((resp: any) => resp),
      catchError(error => {
        console.error('Get Project Settings Error:', error);
        return of(error);
      })
    );
  }
  
  public updateProjectSettings(body: any): Observable<any> {
    return this.http.put(environment.base_url + '/project-configs', body, { observe: 'response' }).pipe(
      map((resp: any) => resp),
      catchError(error => {
        console.error('Update Project Settings Error:', error);
        return of(error);
      })
    );
  }

  // -------------------------------------------Site configuration-----------------------------------
  public getSiteSettings(): Observable<any> {
    return this.http.get(environment.base_url + '/site-config').pipe(
      map((resp: any) => resp),
      catchError(error => {
        console.error('Get Site Settings Error:', error);
        return of(error);
      })
    );
  }
  
  public updateSiteSettings(body: any): Observable<any> {
    return this.http.put(environment.base_url + '/site-configs', body, { observe: 'response' }).pipe(
      map((resp: any) => resp),
      catchError(error => {
        console.error('Update Site Settings Error:', error);
        return of(error);
      })
    );
  }
  getEncryptedTenantId(tenantId: any) {
    return this.http.get(environment.base_url + '/open/tenant-id/encrypt?tenantId=' + tenantId)
      .pipe(
        map((resp: any) => {
          return resp;
        }),
        catchError((error) => {
          return throwError(error);
        })
      ); 
  }

  // -------------------------------------------Billing-----------------------------------
  public getTransactionInfo(
    startCreatedDate: any,
    endCreatedDate: any,
    parameters: any,
  ): Observable<any> {
    let params = new HttpParams();
    
    if (startCreatedDate && endCreatedDate && startCreatedDate !== "" && endCreatedDate !== "") {
      params = params
        .set("startCreatedDate", startCreatedDate)
        .set("endCreatedDate", endCreatedDate);
    }

    if (parameters) {
      params = params
        .set("startFrom", parameters.startRow)
        .set("perPage", "25")
        .set("columnName", parameters.sortModel[0].colId)
        .set("sortOrder", parameters.sortModel[0].sort);
    }

    const url = '/here-map-credit-transactions';

    return this.http.get(`${environment.base_url}${url}`, { params: params }).pipe(
      map((resp: any) => resp),
      catchError((error) => {
        console.error('Get Transaction Info Error:', error);
        return of(error);
      })
    );
  }

  public getPurchaseInfo(
    startValidityDate: any,
    endValidityDate: any,
  ): Observable<any> {
    let params = new HttpParams();
    
    if (startValidityDate && endValidityDate && startValidityDate !== "" && endValidityDate !== "") {
      params = params
        .set("startValidityDate", startValidityDate)
        .set("endValidityDate", endValidityDate);
    }

    const url = '/here-map-credits';

    return this.http.get(`${environment.base_url}${url}`, { params: params }).pipe(
      map((resp: any) => resp),
      catchError((error) => {
        console.error('Get Purchase Info Error:', error);
        return of(error);
      })
    );
  }
  
  public reqTopupCreditsOrUpgradeTier(type: string): Observable<any> {
    let params = new HttpParams();
    
    if (type && type !== "") {
      params = params.set("type", type);
    }
    
    const url = '/here-map-credits/request-credit';
    
    return this.http.get(`${environment.base_url}${url}`, { params: params }).pipe(
      map((resp: any) => resp),
      catchError((error) => {
        console.error('Request Topup Credits Error:', error);
        return of(error);
      })
    );
  }

  // -------------------------------------------Asset configuration-----------------------------------
  public getAssetSettings(): Observable<any> {
    return this.http.get(environment.base_url + '/assets-config').pipe(
      map((resp: any) => resp),
      catchError(error => {
        console.error('Get Asset Settings Error:', error);
        return of(error);
      })
    );
  }
  
  public updateAssetSettings(body: any): Observable<any> {
    return this.http.put(environment.base_url + '/assets-configs', body, { observe: 'response' }).pipe(
      map((resp: any) => {
        this.refreshList$.emit(resp);
        return resp;
      }),
      catchError(error => {
        console.error('Update Asset Settings Error:', error);
        return of(error);
      })
    );
  }
  
  // -------------------------------------------Customer configuration-----------------------------------
  public getCustomerSettings(): Observable<any> {
    return this.http.get(environment.base_url + '/customer-config').pipe(
      map((resp: any) => resp),
      catchError(error => {
        console.error('Get Customer Settings Error:', error);
        return of(error);
      })
    );
  }
  
  public updateCustomerSettings(body: any): Observable<any> {
    return this.http.put(environment.base_url + '/customer-configs', body, { observe: 'response' }).pipe(
      map((resp: any) => {
        this.refreshList$.emit(resp);
        return resp;
      }),
      catchError(error => {
        console.error('Update Customer Settings Error:', error);
        return of(error);
      })
    );
  }
  
  public getIndustrySettings(): Observable<any> {
    return this.http.get(environment.reseller_url + 'public/industries').pipe(
      map((resp: any) => resp),
      catchError(error => {
        console.error('Get Industry Settings Error:', error);
        return of(error);
      })
    );
  }

  public getSetupFeatures(): Observable<any> {
    return this.http.get(environment.base_url + '/setup/features').pipe(
      map((resp: any) => resp),
      catchError(error => {
        console.error('Get Setup Features Error:', error);
        return of(error);
      })
    );
  }
  
  public setSetupFeatures(body: any): Observable<any> {
    return this.http.post(environment.base_url + '/setup/onboarding', body, { observe: 'response' }).pipe(
      map((resp: any) => resp),
      catchError(error => {
        console.error('Set Setup Features Error:', error);
        return of(error);
      })
    );
  }

  public getStripeCustomerPortalUrl(tenantId: any, token: any): Observable<any> {
    let params = new HttpParams()
      .set("tenantId", tenantId)
      .set("token", token);
      
    const headers = new HttpHeaders({
      'token': token
    });

    return this.http.get(environment.reseller_url + 'stripe/create-customer-portal-session', { params, headers }).pipe(
      map((resp: any) => resp),
      catchError(error => {
        console.error('Get Stripe Customer Portal URL Error:', error);
        return of(error);
      })
    );
  }
  
  public getSetupLicenseDetals(): Observable<any> {
    return this.http.get(environment.base_url + '/v2/get-licenses', { observe: 'response' }).pipe(
      map((resp: any) => resp),
      catchError(error => {
        console.error('Get Setup License Details Error:', error);
        return of(error);
      })
    );
  }
  
  public getPaymentLink(packageCode: any, frequency: any, tenantId: any): Observable<any> {
    let params = new HttpParams()
      .set("packageCode", packageCode)
      .set("frequency", frequency)
      .set("tenantId", tenantId);

    return this.http.get(environment.reseller_url + 'accounts/upgrade/payment-link', { observe: 'response', params }).pipe(
      map((resp: any) => resp),
      catchError(error => {
        console.error('Get Payment Link Error:', error);
        return of(error);
      })
    );
  }

  public getPaymentLinkMetaData(tenantId: any, token: any): Observable<any> {
    let params = new HttpParams()
      .set("tenantId", tenantId)
      .set("token", token);
      
    const headers = new HttpHeaders({
      'token': token
    });

    return this.http.get(environment.reseller_url + 'accounts/upgrade/payment-link/meta-data', { params, headers }).pipe(
      map((resp: any) => resp),
      catchError(error => {
        console.error('Get Payment Link Meta Data Error:', error);
        return of(error);
      })
    );
  }
  
  // ------------------------------------------Cancel Subscription----------------------------------
  public cancelSubscription(tenantId: any, token: any, body: any): Observable<any> {
    const headers = new HttpHeaders({
      'token': token
    });
    
    return this.http.put(
      environment.reseller_url + 'customers/tenant/cancel?tenantId=' + tenantId, 
      body, 
      { observe: 'response', headers }
    ).pipe(
      map((resp: any) => resp),
      catchError(error => {
        console.error('Cancel Subscription Error:', error);
        return of(error);
      })
    );
  }
  
  public deleteSubscription(tenantId: any): Observable<any> {
    return this.http.delete(
      environment.base_url + '/customers/tenant?tenantId=' + tenantId, 
      { observe: 'response' }
    ).pipe(
      map((resp: any) => resp),
      catchError(error => {
        console.error('Delete Subscription Error:', error);
        return throwError(() => error);
      })
    );
  }
}